import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { v4 as uuid } from 'uuid';

import { deletePendingPositionApi, putOrderTpSlApi } from '@flipster/apis/v2/trade';
import { T } from '@flipster/i18n';
import { pendingPositionsBunja } from '@flipster/r230522/normal-sub/tables/private/orders';
import { ConditionalOrderTypeEnum } from '@flipster/r230522/original/order';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { useSendApi } from '@flipster/react/misc/use-send-api';
import { dateToEpochString } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';
import { V2OrderSideType } from '@flipster/utils/types/base';

import { orderAttributionAtom } from '../shared/order-attribution';
import { TpSlTriggerDirectionMapper } from './constant';

/**
 * @deprecated Time-trigger orders have been sunset. Legacy delete path for leftover
 * time-trigger TP/SL only. New code must use `useDeleteTpSl`.
 */
export function useDeleteTpSlLegacy() {
  const { ordersMapAtom } = useBunja(pendingPositionsBunja);
  const orders = useAtomValue(ordersMapAtom);
  const attribution = useAtomValue(orderAttributionAtom);

  return useSendApi(
    async (side: V2OrderSideType, tpSlType: 'tp' | 'sl', symbol: string, slot: number, accountId?: number) => {
      try {
        const openPositionOrderType = [ConditionalOrderTypeEnum.PositionSl, ConditionalOrderTypeEnum.PositionTp];
        const currentOrder = orders?.[symbol]?.find((o) => {
          // timetriggerOrder scope out in revamp tpsl

          if (o.slot !== slot) return false;
          // open position의 tp/sl인 경우
          if (openPositionOrderType.includes(o.conditionalOrderType))
            // triggerDirection이 현재 open position의 side에 맞는 tp or sl인지
            return o.triggerDirection === TpSlTriggerDirectionMapper[side][tpSlType];
          // pending order의 tp/sl인 경우
          return true;
        });

        if (!currentOrder) return;

        // open position tp/sl인 경우
        if (openPositionOrderType.includes(currentOrder.conditionalOrderType)) {
          await deletePendingPositionApi(symbol, currentOrder.orderId, {
            requestId: uuid(),
            timestamp: dateToEpochString(new Date()),
            attribution,
            accountId,
          });
        } else {
          // pending order의 tp/sl인 경우
          await putOrderTpSlApi(symbol, currentOrder.orderId, {
            requestId: uuid(),
            timestamp: dateToEpochString(new Date()),
            type: tpSlType === 'tp' ? 'OrderTp' : 'OrderSl',
            attribution,
            accountId,
          });
        }
        snackbar.success({ message: <T id='Position.r250401.Position_update_toast' /> });
      } catch (error) {
        snackbar.error({ message: getErrorMessage(error) });
      }
    },
  );
}
